"use client";

import { cn, handleTrackClickEvent } from "@/lib/utils";
import { useAuth } from "@/providers/auth-provider";
import { useChat } from "@/providers/chat-provider";
import { Menu, Transition } from "@headlessui/react";
import { AlertCircleIcon, ArrowRightIcon } from "lucide-react";
import { signOut } from "next-auth/react";
import Link from "next/link";
import { Fragment } from "react";
import ProfilePicture from "../../profile-picture";

type TUserMenuProps = {
	profileImageUrl?: string;
	isVerified?: boolean;
	isFounder?: boolean;
	username?: string;
	show?: boolean;
};

const UserMenu = ({ username, profileImageUrl, isFounder, isVerified, show }: TUserMenuProps) => {
	const { user } = useAuth();
	const { handleOpenChatWidget } = useChat();

	const handleLogout = () => {
		// Reset analytics to clear the identify event setup
		window.rudderanalytics?.reset();

		signOut();
	};

	return show ? (
		<Menu as="div" className="relative">
			<div>
				{!isVerified && (
					<span className="absolute -right-2 -top-2 z-10">
						<AlertCircleIcon className="h-7 w-7 fill-red-500 stroke-red-50" />
					</span>
				)}

				<Menu.Button className="flex max-w-xs items-center rounded-full bg-white text-sm focus:outline-none">
					<span className="sr-only">Open user menu</span>
					<ProfilePicture
						profilePictureUrl={profileImageUrl}
						className={{
							container: "h-10 w-10 rounded-full p-0.5",
							image: "rounded-full text-gray-600",
							badgeImage: "-bottom-4 h-8 w-8",
						}}
						isVerified={isVerified}
						isFounder={isFounder}
					/>
				</Menu.Button>
			</div>
			<Transition
				as={Fragment}
				enter="transition ease-out duration-200"
				enterFrom="transform opacity-0 scale-95"
				enterTo="transform opacity-100 scale-100"
				leave="transition ease-in duration-75"
				leaveFrom="transform opacity-100 scale-100"
				leaveTo="transform opacity-0 scale-95"
			>
				<Menu.Items className="absolute right-0 z-30 mt-2 w-64 origin-top-right rounded-md bg-white px-4 py-3 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
					{!isVerified && (
						<Menu.Item>
							{({ active }) => (
								<Link
									href="/account/profile"
									onClick={() =>
										handleTrackClickEvent("/account/profile", "Verify from navbar dropdown")
									}
									className={cn(
										active ? "bg-brand-primary/80" : "",
										"mb-2 flex w-full items-center justify-center gap-x-1 rounded-md bg-brand-primary py-3 text-xs font-semibold text-white"
									)}
								>
									<span>Earn Your Verification Badge</span>
									<span>
										<ArrowRightIcon className="h-3 w-3" />
									</span>
								</Link>
							)}
						</Menu.Item>
					)}

					{isVerified && username && (
						<Menu.Item>
							<Link
								href={`/${username}`}
								className={cn("hover:bg-gray-100", "block px-4 py-2 text-sm text-gray-700")}
							>
								My Profile
							</Link>
						</Menu.Item>
					)}

					<Menu.Item>
						<Link
							href="/account/profile"
							className={cn("hover:bg-gray-100", "block px-4 py-2 text-sm text-gray-700")}
						>
							Account
						</Link>
					</Menu.Item>

					<Menu.Item>
						<button
							onClick={handleOpenChatWidget}
							className={cn(
								"hover:bg-gray-100",
								"block w-full px-4 py-2 text-left text-sm text-gray-700"
							)}
						>
							Messages
						</button>
					</Menu.Item>

					{user && (
						<Menu.Item>
							<Link
								href="/dashboard"
								className={cn("hover:bg-gray-100", "block px-4 py-2 text-sm text-gray-700")}
							>
								Admin Dashboard
							</Link>
						</Menu.Item>
					)}

					<Menu.Item>
						<button
							onClick={handleLogout}
							className={cn(
								"hover:bg-gray-100",
								"block w-full px-4 py-2 text-left text-sm text-gray-700"
							)}
						>
							Logout
						</button>
					</Menu.Item>

					<ModeratedChannels />
				</Menu.Items>
			</Transition>
		</Menu>
	) : (
		<></>
	);
};

// COMMUNITIES TODO: Re-implement after communities rewrite
const ModeratedChannels = () => {
	const data = [];

	return data?.length > 0 ? (
		<Menu.Items className="mt-2 border-t pt-4">
			<h4 className="mb-2 px-4 text-xs font-semibold uppercase tracking-widest text-gray-400">
				Admin Dashboard
			</h4>

			<Menu.Item>
				<Link
					href={{ pathname: "/dashboard" }}
					className={cn("hover:bg-gray-100", "block px-4 py-2 text-sm text-gray-700")}
				>
					Admin Dashboard
				</Link>
			</Menu.Item>
		</Menu.Items>
	) : null;
};

export default UserMenu;
